// Desert Storm — Desertstorm variant (FoA default).
// Rules documented in docs/plans/006-ds-plan.md.

import type { DSPlanConfig, DSPhaseDef, DSNonSiloSlot } from './dsTypes';

const meta = {
  key: 'desertstorm' as const,
  name: 'Desertstorm',
  shortDescription:
    'FoA planning model for Desert Storm. Only two Field Hospitals (II & IV) ' +
    'are staffed; the top 4 by M1 stage on the Silo tile as Flex in P1. In P2 ' +
    'only the true Flex (top 2) hold the Silo, the Silo core moves into ' +
    'Arsenal + Mercenary.',
  integratedAt: '2026-07-01',
  rulebookRef: 'docs/plans/006-ds-plan.md',
  highlights: [
    'Two phases: P1 = 0:00 opening, P2 = 10:00 collapse (Silo/Arsenal/Mercenary unlock).',
    'Priorities: Nuclear Silo (CRITICAL), Info Center & Oil Refineries (HIGH), Arsenal/Mercenary/Science Hub/Field Hospital II (MEDIUM), Field Hospital IV (LOWER).',
    'Only FH II & FH IV are staffed. FH I & FH III stay empty — used only as spawn-dependent fallbacks (see tile notes).',
    'In P1 the top 4 by M1 stage on the Silo tile as Flex; in P2 only the top 2 stay on the Silo, the Silo core (ranks 0-1) moves to Arsenal + Mercenary.',
    'Substitutes inherit their starter\'s full route — no independent slots. Flex & Silo core never subbed.',
  ],
};

// Home layout by strength rank. Rank 0 = strongest non-flex (M1).
// Total = 16 non-flex + 4 flex = 20 starters. Ranks 0-1 (Silo core) are
// claimed onto the Silo in every phase via strengthClaims; their homeByRank
// entry is placeholder and never used.
export const homeByRank: DSNonSiloSlot[] = [
  'oilRefinery1',     //
  'oilRefinery2',     //
  'oilRefinery1',     //
  'oilRefinery2',     //
  'infoCenter',       //
  'fieldHospital2',   //
  'fieldHospital4',   //
  'scienceHub',       //
  'infoCenter',
  'infoCenter',
  'oilRefinery1',     //
  'oilRefinery2',     //
  'oilRefinery1',     //
  'oilRefinery2',     //
  'scienceHub',       //
  'fieldHospital2',   //
  'fieldHospital4',   //
  'scienceHub',       //
];

// Silo-core ranks. The top 4 by M1 (2 flex + these 2 non-flex) stage on
// the Silo from Phase 1. Never receive a sub pairing.
const coreRanks = [0, 1] as const;

// Field Hospital contingency notes. Staffed tiles (II & IV) and their
// unstaffed counterparts (I & III) both carry the hint so it's visible
// no matter which tile ends up closer to spawn.
const slotNotes = {
  fieldHospital1: 'FH IV players relocate here if this tile is closer to our spawn.',
  fieldHospital2: 'Relocate to FH III if closer to our spawn.',
  fieldHospital3: 'FH II players relocate here if this tile is closer to our spawn.',
  fieldHospital4: 'Relocate to FH I if closer to our spawn.',
};

export const phases: DSPhaseDef[] = [
  // PHASE 1 — Info Center, Oil Refineries, FH II & FH IV, Science Hub open.
  // Silo/Arsenal/Mercenary and FH I & FH III stay closed. Top 4 by M1
  // (2 Flex + Silo core) stage on the Silo tile from the start; it is
  // rendered as "Flex" until Phase 2 opens the Silo.
  {
    title: 'PHASE 1',
    time: '0:00',
    timeColor: 'text-green-400',
    subtitle: '2 FLEX PLAYERS',
    subtitleColor: 'text-yellow-400',
    priorities: {
      nuclearSilo: 'critical',   // holding position; officially opens in P2
      infoCenter: 'high',
      oilRefinery1: 'high',
      oilRefinery2: 'high',
      arsenal: null,
      mercenary: null,
      scienceHub: 'medium',
      fieldHospital1: 'lower',  // not staffed — see slotNotes
      fieldHospital2: 'lower',
      fieldHospital3: 'lower',   // not staffed — see slotNotes
      fieldHospital4: 'lower',
    },
    capacities: {
      nuclearSilo: 4,        // 2 Flex + 2 Silo core hold here
      infoCenter: 3,
      oilRefinery1: 3,
      oilRefinery2: 3,
      arsenal: 0,
      mercenary: 0,
      scienceHub: 3,
      fieldHospital1: 0,
      fieldHospital2: 2,
      fieldHospital3: 0,
      fieldHospital4: 2,
    },
    // Top 4 by M1 (2 Flex + Silo core) hold the Silo from the start.
    strengthClaims: [{ slot: 'nuclearSilo', count: 4 }],
  },
  // PHASE 2 — Silo opens (Flex + core stay put), Arsenal + Mercenary unlock.
  // FH II shrinks to 2; Oil Refineries and Science Hub shrink by one each.
  // FH I & FH III remain unstaffed (contingency notes stay visible).
  {
    title: 'PHASE 2',
    time: '10:00',
    timeColor: 'text-red-500',
    subtitle: 'SILO OPENS – COLLAPSE & WIN',
    subtitleColor: 'text-red-500',
    priorities: {
      nuclearSilo: 'critical',
      infoCenter: 'high',
      oilRefinery1: 'high',
      oilRefinery2: 'high',
      arsenal: 'medium',
      mercenary: 'medium',
      scienceHub: 'medium',
      fieldHospital1: 'lower',
      fieldHospital2: 'lower',
      fieldHospital3: 'lower',
      fieldHospital4: 'lower',
    },
    capacities: {
      nuclearSilo: 2,
      infoCenter: 3,
      oilRefinery1: 4,
      oilRefinery2: 4,
      arsenal: 0,
      mercenary: 0,
      scienceHub: 3,
      fieldHospital1: 0,
      fieldHospital2: 2,
      fieldHospital3: 0,
      fieldHospital4: 2,
    },
    strengthClaims: [
      { slot: 'nuclearSilo', count: 2 },
    ],
  },
];

export const desertstormConfig: DSPlanConfig = {
  meta,
  phases,
  homeByRank,
  coreRanks,
  slotNotes,
};
